import { Link } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconClock, IconFiles } from '@tabler/icons-react';
import { docsUsable, missingIdsFor, splitMissing, typeNames, useMyDocuments } from '@/lib/documents';
import { documentRules, useCopy } from '@/lib/settings';
import { NudgeCard } from '@/components/member/NudgeCard';

export interface DocumentsNudge {
  /** Something to tell the member (only once GET me/documents has loaded). */
  show: boolean;
  /** True = documents still to upload; false = uploaded and waiting for a reviewer. */
  needsUpload: boolean;
  /** "Driver's licence (back), Vehicle licence disc" */
  names: string;
}

/** Whether Home should show the documents nudge (driver documents count only for drivers). */
export function useDocumentsNudge(isDriver?: boolean): DocumentsNudge {
  const { settings } = useCopy();
  const rules = documentRules(settings);
  const docs = useMyDocuments();
  const view = docs.data;
  if (!rules.enabled || !docs.isSuccess || !docsUsable(view)) return { show: false, needsUpload: false, names: '' };
  const { toUpload, awaitingReview } = splitMissing(view, missingIdsFor(view, 'all', isDriver));
  const pendingReview = (view.approvalRequired ?? rules.approvalRequired) && awaitingReview.length > 0;
  return { show: toUpload.length > 0 || pendingReview, needsUpload: toUpload.length > 0, names: typeNames(toUpload) };
}

/**
 * Home nudge: required documents still to upload, or (when a reviewer must accept them) a "we'll
 * check them soon" note. Links to /account#documents. Renders nothing while loading or when
 * nothing is outstanding.
 */
export function DocumentsNudgeCard({ isDriver }: { isDriver?: boolean }) {
  const { t } = useCopy();
  const nudge = useDocumentsNudge(isDriver);
  if (!nudge.show) return null;
  return nudge.needsUpload ? (
    <NudgeCard
      tone="warn"
      icon={<IconFiles size={24} />}
      title="Upload your documents"
      action={<Button component={Link} to="/account#documents" variant="default" leftSection={<IconFiles size={18} />}>Upload documents</Button>}
    >
      {t('documents.missing', undefined, { documents: nudge.names })}
    </NudgeCard>
  ) : (
    <NudgeCard icon={<IconClock size={24} />} title="Documents waiting for a check">
      {t('docs.approval_pending')}
    </NudgeCard>
  );
}
